/**
 * Click-to-edit scaffolding shared by the cells that edit in place rather than
 * in a popover: save on blur or Enter, discard on Escape, and keep the click
 * off the row underneath, which would otherwise open the brief.
 *
 * The hook owns the draft as text and hands the caller the trimmed string on
 * commit; parsing, clamping and "is this actually a change" stay with the cell,
 * since only it knows what its value means.
 */
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';

interface InlineCellEdit {
  editing: boolean;
  /** Spread onto the `<input>` rendered while editing. */
  inputProps: {
    ref: React.RefObject<HTMLInputElement | null>;
    value: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onBlur: () => void;
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
    onClick: (event: MouseEvent<HTMLInputElement>) => void;
  };
  /** Spread onto the `<button>` shown when not editing. */
  triggerProps: {
    type: 'button';
    onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  };
}

export function useInlineCellEdit(text: string, onCommit: (draft: string) => void): InlineCellEdit {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraft(text);
  }, [text]);

  // Selecting on entry makes the common case — replacing the value outright —
  // a single keystroke.
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    onCommit(draft.trim());
    // A commit the cell declines — 140 clamped back to the 100 already stored,
    // or "abc" in a number cell — leaves `text` untouched, so the resync effect
    // never fires and reopening the editor would show what was rejected.
    setDraft(text);
  };

  return {
    editing,
    inputProps: {
      ref: inputRef,
      value: draft,
      onChange: (event) => setDraft(event.target.value),
      onBlur: commit,
      onKeyDown: (event) => {
        if (event.key === 'Enter') commit();
        else if (event.key === 'Escape') {
          setDraft(text);
          setEditing(false);
        }
      },
      onClick: (event) => event.stopPropagation(),
    },
    triggerProps: {
      type: 'button',
      onClick: (event) => {
        event.stopPropagation();
        setEditing(true);
      },
    },
  };
}
